import { readChoice, readJson, rememberValue, userKey } from "../../browserMemory.js";
import { freePeriod, periodAround } from "./cockpitPeriod.js";
import type { CompareChoice, Period } from "./cockpitPeriod.js";

/**
 * GEDÄCHTNIS DES COCKPITS JE BENUTZER IM BROWSER (Spec „Merken“, Muster S17): Zeitraum, Vergleich und Unternehmen sind eine
 * Bequemlichkeit des Geräts; `browserMemory.ts` fängt einen gesperrten Speicher ab, ein gelesener Wert wird hier geprüft. Der Anker
 * von Monat, Quartal und Jahr wird nicht gemerkt: Beim Öffnen gilt der laufende Abschnitt.
 */

const PERIOD_KEY = "ki-cockpit-period";
const COMPARE_KEY = "ki-cockpit-compare";
const COMPANY_KEY = "ki-cockpit-company";
const PRESETS = ["MONTH", "QUARTER", "YEAR"] as const;
const COMPARE_MODES = ["NONE", "PREVIOUS_PERIOD", "PREVIOUS_YEAR"] as const;
const ALIGNMENTS = ["CALENDAR", "WEEKDAY"] as const;
const ALL = "ALL";

const NO_COMPARE: CompareChoice = { mode: "NONE", align: null };

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

/** Der gemerkte Zeitraum; Vorgabe ist das Jahr um heute. „frei“ behält seine Grenzen, wenn sie noch gelten. */
export function readPeriod(userId: string | undefined, today: string): Period {
  const fallback = periodAround("YEAR", today);
  const saved = readJson(userKey(PERIOD_KEY, userId));
  if (!isRecord(saved)) return fallback;
  const { kind, from, to } = saved;
  if (kind === "FREE") return (typeof from === "string" && typeof to === "string" ? freePeriod(from, to) : null) ?? fallback;
  const preset = PRESETS.find((k) => k === kind);
  return preset === undefined ? fallback : periodAround(preset, today);
}

/** Merkt `{ kind }` bzw. `{ kind: "FREE", from, to }`. Hineinklicken ist kein gewählter Zeitraum: Es zählt der Zeitraum des Rückwegs. */
export function rememberPeriod(userId: string | undefined, p: Period): void {
  const chosen = p.back ?? p;
  rememberValue(userKey(PERIOD_KEY, userId), JSON.stringify(chosen.kind === "FREE"
    ? { kind: chosen.kind, from: chosen.from, to: chosen.to } : { kind: chosen.kind }));
}

/** Der gemerkte Vergleich; Vorgabe ohne Vergleich und ohne bewusste Ausrichtung. */
export function readCompare(userId: string | undefined): CompareChoice {
  const saved = readJson(userKey(COMPARE_KEY, userId));
  if (!isRecord(saved)) return NO_COMPARE;
  const mode = COMPARE_MODES.find((m) => m === saved["mode"]);
  return mode === undefined ? NO_COMPARE : { mode, align: ALIGNMENTS.find((a) => a === saved["align"]) ?? null };
}

/** Die Ausrichtung steht nur nach bewusster Wahl im Speicher (651). */
export function rememberCompare(userId: string | undefined, c: CompareChoice): void {
  rememberValue(userKey(COMPARE_KEY, userId), JSON.stringify(c.align === null ? { mode: c.mode } : { mode: c.mode, align: c.align }));
}

/** Das gemerkte Unternehmen, wenn der Benutzer es noch sieht; sonst „Alle“. */
export function readCompany(userId: string | undefined, visible: readonly string[]): "ALL" | string {
  return readChoice<string>(userKey(COMPANY_KEY, userId), [ALL, ...visible], ALL);
}

export function rememberCompany(userId: string | undefined, v: "ALL" | string): void {
  rememberValue(userKey(COMPANY_KEY, userId), v);
}
